import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AttachmentKind,
  CreateItemInput,
  CreateLotInput,
  InventoryCategory,
  InventoryItem,
  InventoryItemSummary,
  InventoryListQuery,
  InventoryLocation,
  InventoryLot,
  InventoryMovement,
  UseStockInput,
} from '@jupi/shared'
import { api, apiFetch } from '../api/client'

// The inventory's queries and writes, shared by the admin app and the
// maintenance site. Every write invalidates the whole 'inventory' key: the
// lists are club-sized and a change can move an item between filters.

const KEY = ['inventory'] as const

function withQuery(path: string, params: Record<string, string | undefined>) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value)
  const qs = search.toString()
  return qs ? `${path}?${qs}` : path
}

export function useInventoryItems(filter: Partial<InventoryListQuery>) {
  return useQuery({
    queryKey: [...KEY, 'items', filter],
    queryFn: () =>
      api
        .get<{ items: InventoryItemSummary[] }>(withQuery('/api/inventory/items', filter as Record<string, string | undefined>))
        .then((r) => r.items),
    placeholderData: (previous) => previous,
  })
}

export function useInventoryCategories(all = false) {
  return useQuery({
    queryKey: [...KEY, 'categories', all],
    queryFn: () =>
      api
        .get<{ categories: InventoryCategory[] }>(withQuery('/api/inventory/categories', { all: all ? 'true' : undefined }))
        .then((r) => r.categories),
  })
}

export function useInventoryLocations(all = false) {
  return useQuery({
    queryKey: [...KEY, 'locations', all],
    queryFn: () =>
      api
        .get<{ locations: InventoryLocation[] }>(withQuery('/api/inventory/locations', { all: all ? 'true' : undefined }))
        .then((r) => r.locations),
  })
}

export function useInventoryTags() {
  return useQuery({
    queryKey: [...KEY, 'tags'],
    queryFn: () => api.get<{ tags: { tag: string; count: number }[] }>('/api/inventory/tags').then((r) => r.tags),
  })
}

export function useInventoryMovements(itemId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'movements', itemId],
    enabled: itemId !== null,
    queryFn: () =>
      api
        .get<{ movements: InventoryMovement[] }>(withQuery('/api/inventory/movements', { itemId: itemId ?? undefined, limit: '20' }))
        .then((r) => r.movements),
  })
}

export interface FleetAircraft {
  registration: string
  aircraftType: string | null
}

// The fleet, for "fits this aircraft". Every signed-in member may read it.
export function useFleetAircraft() {
  return useQuery({
    queryKey: ['inventory', 'fleet'],
    queryFn: () => api.get<{ aircraft: FleetAircraft[] }>('/api/aircraft').then((r) => r.aircraft),
    staleTime: 5 * 60_000,
  })
}

export type ItemBody = Omit<CreateItemInput, 'lot'> & { lot?: CreateLotInput }

// One hook for every write, so each screen gets the same invalidation.
export function useInventoryWrites() {
  const queryClient = useQueryClient()
  const done = () => queryClient.invalidateQueries({ queryKey: KEY })
  return {
    createItem: useMutation({
      mutationFn: (body: ItemBody) => api.post<{ item: InventoryItemSummary }>('/api/inventory/items', body),
      onSuccess: done,
    }),
    updateItem: useMutation({
      mutationFn: ({ itemId, ...body }: { itemId: string } & Partial<Omit<InventoryItem, 'itemId' | 'fitments' | 'lots'>> & {
        archived?: boolean
        fitments?: { registration?: string; aircraftType?: string }[]
      }) => api.patch<{ item: InventoryItemSummary }>(`/api/inventory/items/${itemId}`, body),
      onSuccess: done,
    }),
    deleteItem: useMutation({ mutationFn: (itemId: string) => api.delete(`/api/inventory/items/${itemId}`), onSuccess: done }),
    createLot: useMutation({
      mutationFn: ({ itemId, ...body }: { itemId: string } & CreateLotInput) =>
        api.post<{ lot: InventoryLot }>(`/api/inventory/items/${itemId}/lots`, body),
      onSuccess: done,
    }),
    updateLot: useMutation({
      mutationFn: ({ lotId, ...body }: { lotId: string } & Partial<CreateLotInput>) =>
        api.patch<{ lot: InventoryLot }>(`/api/inventory/lots/${lotId}`, body),
      onSuccess: done,
    }),
    deleteLot: useMutation({ mutationFn: (lotId: string) => api.delete(`/api/inventory/lots/${lotId}`), onSuccess: done }),
    useStock: useMutation({
      mutationFn: ({ lotId, ...body }: { lotId: string } & UseStockInput) =>
        api.post<{ lot: InventoryLot }>(`/api/inventory/lots/${lotId}/use`, body),
      onSuccess: done,
    }),
    uploadAttachment: useMutation({
      mutationFn: ({ lotId, kind, file }: { lotId: string; kind: AttachmentKind; file: File }) => {
        const body = new FormData()
        body.append('file', file)
        body.append('kind', kind)
        return apiFetch<{ lot: InventoryLot }>(`/api/inventory/lots/${lotId}/attachments`, { method: 'POST', body })
      },
      onSuccess: done,
    }),
    deleteAttachment: useMutation({
      mutationFn: (attachmentId: string) => api.delete(`/api/inventory/attachments/${attachmentId}`),
      onSuccess: done,
    }),
    saveCategory: useMutation({
      mutationFn: ({ categoryKey, create, ...body }: { categoryKey: string; create?: boolean } & Record<string, unknown>) =>
        create ? api.post('/api/inventory/categories', { categoryKey, ...body }) : api.patch(`/api/inventory/categories/${categoryKey}`, body),
      onSuccess: done,
    }),
    saveLocation: useMutation({
      mutationFn: ({ locationId, ...body }: { locationId?: string } & Record<string, unknown>) =>
        locationId ? api.patch(`/api/inventory/locations/${locationId}`, body) : api.post('/api/inventory/locations', body),
      onSuccess: done,
    }),
  }
}
